import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import appConfig from "services/apiConfig";
import { storageService } from "services/storageService";
import { useToaster } from "contexts/toasterContext";
import apiService from "services/apiService";
import {
  getPostLoginRedirectPath,
  logAuthPayload,
} from "util/moduleAccess";

// ── API response types ────────────────────────────────────────────────────────
interface Tenant {
  subdomainName: string;
}

interface DiscoverTenantsResponse {
  tenants: Tenant[];
  count: number;
}

interface SelectTenantResponse {
  token: string;
  message: string;
  refreshToken?: string;
}

interface PasswordLoginState {
  mode?: "password-login";
  email?: string;
  password?: string;
  organizations?: string[];
}

interface PasswordLoginResponse {
  accessToken?: string;
  token?: string;
  message?: string;
  refreshToken?: string;
}

// ── Raw axios calls (discovery token never touches localStorage) ──────────────
function discoverTenants(discoveryToken: string): Promise<DiscoverTenantsResponse> {
  return axios
    .get(`${appConfig.getBaseUrl()}api/auth/discover-tenants`, {
      headers: { Authorization: `Bearer ${discoveryToken}` },
    })
    .then((res) => res.data);
}

function selectTenant(
  discoveryToken: string,
  subdomain: string
): Promise<SelectTenantResponse> {
  return axios
    .post(
      `${appConfig.getBaseUrl()}api/auth/select-tenant`,
      {},
      {
        params: { subdomain },
        headers: { Authorization: `Bearer ${discoveryToken}` },
      }
    )
    .then((res) => res.data);
}

// ─────────────────────────────────────────────────────────────────────────────

export default function SelectOrganization() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToaster();

  const discoveryToken = searchParams.get("discovery_token");
  const urlToken = searchParams.get("token");
  const urlRefreshToken = searchParams.get("refreshToken");

  const loginState = (location.state as PasswordLoginState) || {};

  const passwordLoginEmail =
    loginState?.email || sessionStorage.getItem("loginEmail") || "";
  const passwordLoginPassword =
    loginState?.password || sessionStorage.getItem("loginPassword") || "";
  const passwordOrgsFromState =
    loginState?.organizations ||
    JSON.parse(sessionStorage.getItem("passwordOrganizations") || "[]");
  const isPasswordLoginFlow =
    loginState?.mode === "password-login" &&
    !!passwordLoginEmail &&
    !!passwordLoginPassword;

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState<string | null>(null);

  useEffect(() => {
    if (isPasswordLoginFlow) {
      const fallbackTenants = (passwordOrgsFromState || []).map((subdomainName: string) => ({
        subdomainName,
      }));
      setTenants(fallbackTenants);
      setLoading(false);
    } else if (discoveryToken) {
      discoverTenants(discoveryToken)
        .then((res) => setTenants(res.tenants))
        .catch((error) => {
          console.log("error:", error);
          addToast("Failed to load organizations.", "error");
          navigate("/login");
        })
        .finally(() => setLoading(false));
    } else if (urlToken) {
      storageService.removeItem("isMultiOrg");
      storageService.setItem("accessToken", urlToken);

      // --- THE MAGIC FOR BOTH LOCALHOST & SERVER ---
      // 1. Try to get it from the query params (perfect for localhost)
      // 2. Fall back to reading the hostname (perfect for production/server)
      const subdomainFromUrl = searchParams.get("subDomain") || searchParams.get("subdomain");
      console.log("This is sub domain from URL:", subdomainFromUrl);
      console.log("This is resolved sub domain:", subdomainFromUrl);

      // Store in localStorage — survives browser close/reopen and is shared across tabs
      if (subdomainFromUrl) {
        storageService.setItem("activeSubdomain", subdomainFromUrl);
      }

      // Store refreshToken in localStorage so token refresh works after browser restart
      if (urlRefreshToken) {
        storageService.setItem("refreshToken", urlRefreshToken);
      }

      logAuthPayload(urlToken, "single-org-url-token");
      setLoading(false);
      navigate(getPostLoginRedirectPath(urlToken));
    } else {
      setLoading(false);
      navigate("/login");
    }
  }, [addToast, discoveryToken, isPasswordLoginFlow, navigate, urlToken, urlRefreshToken, searchParams]);

  const handleSelectOrg = (subdomainName: string) => {
    if (selecting) return;
    setSelecting(subdomainName);

    if (isPasswordLoginFlow) {
      apiService
        .post("api/login", {
          email: passwordLoginEmail.trim().toLowerCase(),
          password: passwordLoginPassword,
          subDomain: subdomainName,
        })
        .then((res) => {
          const data: PasswordLoginResponse | string = res.data;
          const token = typeof data === "string" ? data : data?.accessToken || data?.token;
          const refreshToken = typeof data === "string" ? null : data?.refreshToken;

          if (!token) {
            throw new Error("Invalid login response");
          }

          storageService.setItem("accessToken", token);
          storageService.setItem("isMultiOrg", "true");
          storageService.setItem("authLoginMode", "password");

          // activeSubdomain and refreshToken go to localStorage (persist across browser restarts)
          // loginEmail / loginPassword / passwordOrganizations stay in sessionStorage (login-flow only)
          storageService.setItem("activeSubdomain", subdomainName);
          sessionStorage.setItem("loginEmail", passwordLoginEmail.trim().toLowerCase());
          sessionStorage.setItem("loginPassword", passwordLoginPassword);
          sessionStorage.setItem("passwordOrganizations", JSON.stringify(passwordOrgsFromState));

          if (refreshToken) {
            storageService.setItem("refreshToken", refreshToken);
          }

          logAuthPayload(token, "password-org-selection");

          addToast(
            (typeof data === "string" ? "" : data?.message) || "Logged in successfully",
            "success"
          );
          navigate(getPostLoginRedirectPath(token));
        })
        .catch((error) => {
          // Trigger toast immediately instead of passing state
          const serverMessage = error?.response?.data?.message || "Authentication failed. Please try again.";
          addToast(serverMessage, "error");

          // Cleanup storage to ensure a clean redirect
          sessionStorage.removeItem("loginPassword");
          sessionStorage.removeItem("passwordOrganizations");

          navigate("/login");
        })
        .finally(() => {
          // Guaranteed to clear the loader no matter what happens
          setSelecting(null);
        });

      return;
    }

    if (!discoveryToken) {
      setSelecting(null);
      return;
    }

    selectTenant(discoveryToken, subdomainName)
      .then((res) => {
        storageService.setItem("accessToken", res.token);
        // Store in localStorage — survives browser close/reopen and new tabs
        storageService.setItem("activeSubdomain", subdomainName);
        if (res.refreshToken) {
          storageService.setItem("refreshToken", res.refreshToken);
        }

        storageService.setItem("isMultiOrg", "true");
        storageService.setItem("authLoginMode", "google");
        logAuthPayload(res.token, "oauth-org-selection");

        addToast(res.message || "Logged in successfully", "success");
        navigate(getPostLoginRedirectPath(res.token));
      })
      .catch((error) => {
        const serverMessage = error?.response?.data?.message || "Session expired or failed to select organization.";
        addToast(serverMessage, "error");
        navigate("/login");
      })
      .finally(() => {
        // Guaranteed to clear the loader no matter what happens
        setSelecting(null);
      });
  };

  const getInitials = (name: string) =>
    name
      .split(/[\s-_]/)
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 mb-4 shadow-lg">
            <svg
              className="w-7 h-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Choose an Organization
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Your account is linked to multiple organizations.
            <br />
            Select one to continue.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <svg className="animate-spin w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading organizations…</p>
            </div>
          ) : tenants.length === 0 ? (
            <div className="py-16 text-center text-gray-500 dark:text-gray-400 text-sm">
              No organizations found for your account.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {tenants.map((tenant) => {
                const isSelecting = selecting === tenant.subdomainName;
                return (
                  <li key={tenant.subdomainName}>
                    <button
                      onClick={() => handleSelectOrg(tenant.subdomainName)}
                      disabled={!!selecting}
                      className="w-full flex items-center gap-4 px-6 py-4 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed group"
                    >
                      {/* Avatar */}
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                          {getInitials(tenant.subdomainName)}
                        </span>
                      </div>

                      {/* Name */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate capitalize">
                          {tenant.subdomainName}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                          {tenant.subdomainName}.liriworks.com
                        </p>
                      </div>

                      {/* Arrow / spinner */}
                      {isSelecting ? (
                        <svg className="animate-spin w-4 h-4 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0"
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Back to login */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/login")}
            className="text-sm text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
          >
            ← Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
}